import { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "sbbb_cookie_consent_v1";

function loadConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveConsent(value) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...value, ts: Date.now() }));
  } catch {
    /* ignore */
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({
    essential: true, // always on
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const existing = loadConsent();
    if (!existing) setVisible(true);
  }, []);

  const acceptAll = () => {
    saveConsent({ essential: true, analytics: true, marketing: true, choice: "accept_all" });
    setVisible(false);
  };
  const rejectAll = () => {
    saveConsent({ essential: true, analytics: false, marketing: false, choice: "reject_non_essential" });
    setVisible(false);
  };
  const savePrefs = () => {
    saveConsent({ ...prefs, choice: "custom" });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-0 inset-x-0 z-[100] p-3 sm:p-5"
    >
      <div className="max-w-5xl mx-auto bg-[#141414] border border-white/15 rounded-2xl shadow-2xl overflow-hidden max-h-[85vh] overflow-y-auto">
        <div className="p-5 sm:p-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="shrink-0 w-12 h-12 rounded-full bg-brand-orange/15 text-brand-orange flex items-center justify-center">
            <Cookie size={22} />
          </div>
          <div className="flex-1">
            <h3 className="font-subheading font-bold uppercase tracking-widest text-white text-sm">
              We use cookies
            </h3>
            <p className="mt-1 font-body text-white/65 text-sm leading-relaxed">
              We use essential cookies to make our site work, plus optional analytics &amp; marketing
              cookies to improve your experience. You can accept, reject non-essentials, or set
              your preferences. See our{" "}
              <a href="/cookies" className="text-brand-orange hover:underline">Cookie Policy</a>.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button
              data-testid="cookie-reject"
              onClick={rejectAll}
              className="font-subheading font-bold uppercase tracking-widest text-xs px-5 py-3 rounded-full border border-white/20 text-white/80 hover:bg-white/5 transition"
            >
              Reject Non-Essential
            </button>
            <button
              data-testid="cookie-prefs-toggle"
              onClick={() => setShowPrefs((v) => !v)}
              className="font-subheading font-bold uppercase tracking-widest text-xs px-5 py-3 rounded-full border border-white/20 text-white/80 hover:bg-white/5 transition"
            >
              Manage Preferences
            </button>
            <button
              data-testid="cookie-accept"
              onClick={acceptAll}
              className="font-subheading font-bold uppercase tracking-widest text-xs px-5 py-3 rounded-full bg-brand-orange text-white hover:bg-[#FF7A1A] transition shadow-[0_4px_20px_0_rgba(255,102,0,0.4)]"
            >
              Accept All
            </button>
          </div>
        </div>

        {showPrefs && (
          <div
            data-testid="cookie-prefs-panel"
            className="border-t border-white/10 p-5 sm:p-6 bg-[#0F0F0F]"
          >
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-subheading font-bold uppercase tracking-widest text-white text-xs">
                Your preferences
              </h4>
              <button
                onClick={() => setShowPrefs(false)}
                aria-label="Close preferences"
                className="text-white/60 hover:text-white"
                data-testid="cookie-prefs-close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3">
              <PrefRow
                title="Essential"
                desc="Required for the site to function. Always on."
                checked={true}
                disabled
                testId="cookie-pref-essential"
              />
              <PrefRow
                title="Analytics"
                desc="Anonymous usage stats to improve the site."
                checked={prefs.analytics}
                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                testId="cookie-pref-analytics"
              />
              <PrefRow
                title="Marketing"
                desc="Personalised ads and remarketing."
                checked={prefs.marketing}
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                testId="cookie-pref-marketing"
              />
            </div>

            <div className="mt-5 flex justify-end">
              <button
                data-testid="cookie-prefs-save"
                onClick={savePrefs}
                className="font-subheading font-bold uppercase tracking-widest text-xs px-6 py-3 rounded-full bg-brand-green text-black hover:bg-[#5EE65E] transition"
              >
                Save Preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PrefRow({ title, desc, checked, onChange, disabled, testId }) {
  return (
    <label
      className={`flex items-start gap-4 p-3 rounded-xl border border-white/10 bg-white/[0.02] ${
        disabled ? "opacity-70" : "cursor-pointer hover:bg-white/[0.04]"
      }`}
      data-testid={testId}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.checked)}
        className="mt-1 h-5 w-5 accent-[#FF6600] cursor-pointer disabled:cursor-not-allowed"
      />
      <div>
        <div className="font-subheading font-bold uppercase tracking-widest text-white text-xs">{title}</div>
        <div className="font-body text-white/55 text-sm mt-0.5">{desc}</div>
      </div>
    </label>
  );
}
